import {
  Grid,
  InputAdornment,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  Paper,
  Button,
  Typography,
  Box,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Loading from '../../../components/loading';
import SuccessMessage from '../../../components/successMessage';
import ErrorMessage from '../../../components/errorMessage';
import IMask from 'imask';
import { usePatient } from '../../hooks/usePatient';
import { useSavePatient } from '../../hooks/useSavePatient';
import { useDeactivatePatient } from '../../hooks/useDeactivatePatient';
import { useCityCatalog } from '../../../hooks/useCityCatalog';
import CityFormModal from '../../../register/cities/cityFormModal';
import DeactivatePatientDialog from './deactivatePatientDialog';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import Alert from '@mui/material/Alert';
import { PersonAdd, PersonOff, WhatsApp } from '@mui/icons-material';

export default function GeneralData({ onSaveSuccess, patient, onPatientLoaded }) {
  const { patientId } = useParams();

  const navigate = useNavigate();

  const cpfMask = useRef(null);
  const responsibleCpfMask = useRef(null);
  const phoneNumberMask = useRef(null);
  const responsiblePhoneNumberMask = useRef(null);

  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState(null);
  const [gender, setGender] = useState('');
  const [cpf, setCpf] = useState('');
  const [rg, setRg] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [street, setStreet] = useState('');
  const [streetNumber, setStreetNumber] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [selectedCityId, setSelectedCityId] = useState('');
  const [addressComplement, setAddressComplement] = useState('');
  const [indicatedBy, setIndicatedBy] = useState('');
  const [previousDentist, setPreviousDentist] = useState('');
  const [emergencyContact, setEmergencyContact] = useState('');
  const [notes, setNotes] = useState('');
  const [firstAppointmentCredit, setFirstAppointmentCredit] = useState('0');
  const [responsibleName, setResponsibleName] = useState('');
  const [responsibleBirthDate, setResponsibleBirthDate] = useState(null);
  const [responsibleCpf, setResponsibleCpf] = useState('');
  const [responsiblePhone, setResponsiblePhone] = useState('');
  const [responsibleKinship, setResponsibleKinship] = useState('');
  const [patientStatus, setPatientStatus] = useState('');
  const [isDeactivateDialogOpen, setDeactivateDialogOpen] = useState(false);

  const { cities: citiesToComboBox, refetch: refetchCities } = useCityCatalog();
  const [isCityFormOpen, setCityFormOpen] = useState(false);
  const [cityInputValue, setCityInputValue] = useState('');
  const {
    patient: fetchedPatient,
    isLoading: isPatientLoading,
    isStale: isPatientStale,
    updatedAt: patientUpdatedAt,
  } = usePatient(patientId);
  const { save: savePatientRequest } = useSavePatient();
  const { deactivate: deactivatePatientRequest } = useDeactivatePatient();

  useEffect(() => {
    if (!fetchedPatient) {
      return;
    }
    setName(fetchedPatient.nome);
    setBirthDate(dayjs(fetchedPatient.dataDeNascimento, 'DD/MM/YYYY'));
    setGender(fetchedPatient.sexo);
    setCpf(fetchedPatient.cpf);
    setRg(fetchedPatient.rg);
    setPhoneNumber(fetchedPatient.telefone);
    setEmail(fetchedPatient.email);
    setStreet(fetchedPatient.rua);
    setStreetNumber(fetchedPatient.numero);
    setNeighborhood(fetchedPatient.bairro);
    setSelectedCityId(fetchedPatient.cityCatalogId ?? '');
    setAddressComplement(fetchedPatient.complementoDeEndereco);
    setIndicatedBy(fetchedPatient.nomeDeQuemIndicou);
    setPreviousDentist(fetchedPatient.nomeDoCirurgiaoDentistaAnterior);
    setEmergencyContact(fetchedPatient.contatoDeEmergencia);
    setNotes(fetchedPatient.anotacoes);
    setFirstAppointmentCredit(fetchedPatient.creditoDaPrimeiraConsulta);
    setResponsibleName(fetchedPatient.nomeDoResponsavel);
    setResponsibleBirthDate(
      fetchedPatient.dataDeNascimentoDoResponsavel
        ? dayjs(fetchedPatient.dataDeNascimentoDoResponsavel, 'DD/MM/YYYY')
        : null,
    );
    setResponsibleCpf(fetchedPatient.cpfDoResponsavel);
    setResponsiblePhone(fetchedPatient.telefoneDoResponsavel);
    setResponsibleKinship(fetchedPatient.parentescoDoResponsavel);
    setPatientStatus(fetchedPatient.status);

    if (onPatientLoaded) {
      onPatientLoaded(fetchedPatient);
    }
  }, [fetchedPatient, onPatientLoaded]);

  const selectedCity = citiesToComboBox.find((city) => city.id === selectedCityId) ?? null;

  const openCityForm = () => setCityFormOpen(true);

  const closeCityForm = () => setCityFormOpen(false);

  const handleCityCreated = (savedCity) => {
    refetchCities().then((freshCities) => {
      const created = (freshCities ?? []).find(
        (city) => city.name === savedCity.name && city.state === savedCity.state,
      );
      if (created) setSelectedCityId(created.id);
    });
  };

  const cleanFields = useCallback(() => {
    setName('');
    setBirthDate(null);
    setGender('');
    setCpf('');
    setRg('');
    setPhoneNumber('');
    setEmail('');
    setStreet('');
    setStreetNumber('');
    setNeighborhood('');
    setSelectedCityId('');
    setCityInputValue('');
    setAddressComplement('');
    setIndicatedBy('');
    setPreviousDentist('');
    setEmergencyContact('');
    setNotes('');
    setFirstAppointmentCredit('0');
    setResponsibleName('');
    setResponsibleBirthDate(null);
    setResponsibleCpf('');
    setResponsiblePhone('');
    setResponsibleKinship('');
    setPatientStatus('');
  }, []);

  const applyMaskDefinition = useCallback(() => {
    const masks = [];
    const maskToCPF = '000.000.000-00';
    const maskToPhoneNumber = '(00) 00000-0000';

    if (cpfMask.current) {
      const mask = IMask(cpfMask.current, { mask: maskToCPF });
      mask.on('accept', () => setCpf(mask.value));
      masks.push(mask);
    }

    if (responsibleCpfMask.current) {
      const mask = IMask(responsibleCpfMask.current, { mask: maskToCPF });
      mask.on('accept', () => setResponsibleCpf(mask.value));
      masks.push(mask);
    }

    if (phoneNumberMask.current) {
      const mask = IMask(phoneNumberMask.current, { mask: maskToPhoneNumber });
      mask.on('accept', () => setPhoneNumber(mask.value));
      masks.push(mask);
    }

    if (responsiblePhoneNumberMask.current) {
      const mask = IMask(responsiblePhoneNumberMask.current, { mask: maskToPhoneNumber });
      mask.on('accept', () => setResponsiblePhone(mask.value));
      masks.push(mask);
    }

    return () => masks.forEach((mask) => mask.destroy());
  }, []);

  useEffect(() => {
    if (patient) {
      setName(patient.nome);
    } else {
      cleanFields();
    }
  }, [patient, cleanFields]);

  useEffect(() => {
    applyMaskDefinition();
  }, [applyMaskDefinition]);

  const savePatient = (e) => {
    e.preventDefault();

    if (!name || !name.trim()) {
      setShowErrorMessage(true);
      setErrorMessage('O campo "Nome" é obrigatório.');
      return;
    }

    setLoading(true);

    const patient = {
      id: patientId,
      nome: name,
      cpf: cpf,
      rg: rg,
      sexo: gender,
      dataDeNascimento: birthDate,
      telefone: phoneNumber,
      email: email,
      rua: street,
      numero: streetNumber,
      bairro: neighborhood,
      cityCatalogId: selectedCityId || null,
      complementoDeEndereco: addressComplement,
      nomeDoResponsavel: responsibleName,
      dataDeNascimentoDoResponsavel: responsibleBirthDate,
      cpfDoResponsavel: responsibleCpf,
      telefoneDoResponsavel: responsiblePhone,
      parentescoDoResponsavel: responsibleKinship,
      nomeDeQuemIndicou: indicatedBy,
      contatoDeEmergencia: emergencyContact,
      anotacoes: notes,
      nomeDoCirurgiaoDentistaAnterior: previousDentist,
      creditoDaPrimeiraConsulta: firstAppointmentCredit,
    };

    if (patientId) {
      savePatientRequest(patient)
        .then((returnFromApi) => {
          setLoading(false);
          setShowSuccessMessage(true);
          setSuccessMessage(returnFromApi.data);
        })
        .catch((returnFromApi) => {
          setLoading(false);
          console.error('ops! ocorreu um erro' + returnFromApi);
          setErrorMessage(returnFromApi.response?.data);
          if (returnFromApi.status === 401) {
            navigate('/expired-session');
          }
        });
    } else {
      savePatientRequest(patient)
        .then((returnFromApi) => {
          setLoading(false);
          setShowSuccessMessage(true);
          setSuccessMessage(returnFromApi.data.message);
          navigate(`/patient-form/${returnFromApi.data.patientId}`);
        })
        .catch((returnFromApi) => {
          setShowErrorMessage(true);
          setErrorMessage(returnFromApi.response?.data);
          setLoading(false);
          console.error(returnFromApi);
        });
    }
  };

  const handleNewPatientClick = () => {
    // Same route (/patient-form/:patientId?) keeps the component mounted, so the fields must be cleared manually.
    cleanFields();
    navigate('/patient-form');
  };

  const handleDeactivateConfirm = () => {
    setDeactivateDialogOpen(false);
    setLoading(true);
    deactivatePatientRequest(patientId)
      .then((returnFromApi) => {
        setLoading(false);
        setPatientStatus('INACTIVE');
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      });
  };

  const handleWhatsAppClick = () => {
    const phoneNumberFormatted = phoneNumber.replace(/\D/g, '');
    const fullPhoneNumber = `55${phoneNumberFormatted}`;

    window.open(`https://web.whatsapp.com/send/?phone=${fullPhoneNumber}`, '_blank');
  };

  return (
    <Box component="form" onSubmit={savePatient}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
        Dados do Paciente
      </Typography>
      <Loading isToHidden={isLoading === false && isPatientLoading === false} />
      {isPatientStale && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Sem conexão com o servidor. Exibindo os últimos dados salvos do prontuário
          {patientUpdatedAt ? ` (${dayjs(patientUpdatedAt).format('DD/MM/YYYY HH:mm')})` : ''}.
          Alterações não podem ser salvas offline.
        </Alert>
      )}
      {patientStatus === 'INACTIVE' && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Paciente desativado.
        </Alert>
      )}
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Grid container rowSpacing={1.5} columnSpacing={2}>
        <Grid size={3}>
          <TextField
            data-cy="patient-name"
            fullWidth
            label="Nome"
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Grid>

        <Grid size={2}>
          <TextField
            label="CPF"
            value={cpf}
            inputRef={cpfMask}
            onChange={(e) => setCpf(e.target.value)}
          />
        </Grid>

        <Grid size={2.7}>
          <DatePicker
            label="Data de Nascimento"
            value={birthDate}
            onChange={(newValue) => setBirthDate(newValue)}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </Grid>

        <Grid size={2.2}>
          <TextField
            fullWidth
            label="RG"
            name="rg"
            value={rg}
            onChange={(e) => setRg(e.target.value)}
          />
        </Grid>

        <Grid size={2}>
          <FormControl fullWidth>
            <InputLabel>Sexo</InputLabel>
            <Select
              label="Sexo"
              name="gender"
              value={gender ?? ''}
              onChange={(e) => setGender(e.target.value)}
            >
              <MenuItem value="MASCULINO">Masculino</MenuItem>
              <MenuItem value="FEMININO">Feminino</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid size={3}>
          <TextField
            fullWidth
            label="Rua"
            name="street"
            value={street}
            onChange={(e) => setStreet(e.target.value)}
          />
        </Grid>

        <Grid size={1.7}>
          <TextField
            fullWidth
            label="Número"
            name="number"
            value={streetNumber}
            onChange={(e) => setStreetNumber(e.target.value)}
          />
        </Grid>

        <Grid size={2.3}>
          <TextField
            fullWidth
            label="Bairro"
            name="neighborhood"
            value={neighborhood}
            onChange={(e) => setNeighborhood(e.target.value)}
          />
        </Grid>

        <Grid size={2.4}>
          <Autocomplete
            size="small"
            fullWidth
            options={citiesToComboBox}
            getOptionLabel={(option) => `${option.name} - ${option.state}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedCity ?? null}
            onInputChange={(_, newInputValue) => setCityInputValue(newInputValue)}
            onChange={(_, newValue) => setSelectedCityId(newValue?.id ?? '')}
            noOptionsText="Nenhuma cidade encontrada."
            PaperComponent={({ children, ...other }) => (
              <Paper {...other}>
                {children}
                <Box
                  sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <Button fullWidth size="small" onClick={openCityForm}>
                    + Nova Cidade
                  </Button>
                </Box>
              </Paper>
            )}
            renderInput={(params) => (
              <TextField {...params} data-cy="city-select" label="Cidade" fullWidth />
            )}
          />
        </Grid>

        <Grid size={2.5}>
          <TextField
            fullWidth
            label="Complemento"
            name="complement"
            value={addressComplement}
            onChange={(e) => setAddressComplement(e.target.value)}
          />
        </Grid>

        <Grid size={2.7}>
          <TextField
            fullWidth
            label="Telefone"
            name="phone"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            inputRef={phoneNumberMask}
          />
        </Grid>

        <Grid size={3}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Grid>

        <Grid size={3.1}>
          <TextField
            fullWidth
            label="Indicado por"
            name="indicatedBy"
            value={indicatedBy}
            onChange={(e) => setIndicatedBy(e.target.value)}
          />
        </Grid>

        <Grid size={3.2}>
          <TextField
            fullWidth
            label="Profissional Anterior"
            name="previousDentist"
            value={previousDentist}
            onChange={(e) => setPreviousDentist(e.target.value)}
          />
        </Grid>

        <Grid size={2.4}>
          <TextField
            fullWidth
            label="Crédito da primeira consulta"
            type="number"
            name="firstConsultationCredit"
            value={firstAppointmentCredit}
            onChange={(e) => setFirstAppointmentCredit(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">R$</InputAdornment>,
            }}
          />
        </Grid>

        <Grid size={9.5} />

        <Grid size={5.95}>
          <TextField
            fullWidth
            label="Em caso de emergência, contatar"
            name="emergencyContact"
            value={emergencyContact}
            onChange={(e) => setEmergencyContact(e.target.value)}
            multiline
            rows={2}
          />
        </Grid>

        <Grid size={5.95}>
          <TextField
            fullWidth
            label="Anotações"
            name="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={2}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 1 }}>
        <Typography variant="h6" component="h2" sx={{ mb: 1, fontWeight: 'bold' }}>
          Dados do Responsável
        </Typography>

        <Grid container rowSpacing={1.5} columnSpacing={2}>
          <Grid size={3.2}>
            <TextField
              fullWidth
              label="Nome do Responsável"
              name="responsibleName"
              value={responsibleName}
              onChange={(e) => setResponsibleName(e.target.value)}
            />
          </Grid>

          <Grid size={3.85}>
            <DatePicker
              label="Data de Nascimento do Responsável"
              value={responsibleBirthDate}
              onChange={(newValue) => setResponsibleBirthDate(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>

          <Grid size={2.5}>
            <TextField
              fullWidth
              label="CPF do Responsável"
              name="responsibleCpf"
              value={responsibleCpf}
              onChange={(e) => setResponsibleCpf(e.target.value)}
              inputRef={responsibleCpfMask}
            />
          </Grid>

          <Grid size={2.35}>
            <TextField
              fullWidth
              label="Telefone"
              name="responsiblePhone"
              value={responsiblePhone}
              onChange={(e) => setResponsiblePhone(e.target.value)}
              inputRef={responsiblePhoneNumberMask}
            />
          </Grid>

          <Grid size={11.9}>
            <TextField
              fullWidth
              label="Parentesco"
              name="responsibleKinship"
              value={responsibleKinship}
              onChange={(e) => setResponsibleKinship(e.target.value)}
            />
          </Grid>
        </Grid>
      </Box>

      <Box
        sx={{
          mt: 1,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          py: 1,
          zIndex: 1,
        }}
      >
        {patientId && patientStatus !== 'INACTIVE' && (
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDeactivateDialogOpen(true)}
            startIcon={<PersonOff />}
            disabled={isPatientStale}
          >
            Desativar Paciente
          </Button>
        )}
        <Button variant="outlined" onClick={handleNewPatientClick} startIcon={<PersonAdd />}>
          Cadastrar Novo Paciente
        </Button>
        <Button
          variant="outlined"
          color="success"
          onClick={handleWhatsAppClick}
          startIcon={<WhatsApp />}
          disabled={!phoneNumber}
        >
          Chamar no WhatsApp
        </Button>
        <Button data-cy="save-patient-btn" variant="contained" type="submit">
          Salvar
        </Button>
      </Box>

      <DeactivatePatientDialog
        open={isDeactivateDialogOpen}
        patientName={name}
        onCancel={() => setDeactivateDialogOpen(false)}
        onConfirm={handleDeactivateConfirm}
      />

      <CityFormModal
        open={isCityFormOpen}
        onClose={closeCityForm}
        onSave={handleCityCreated}
        initialName={selectedCity ? '' : cityInputValue}
      />
    </Box>
  );
}
